// src/features/feeds/components/FeedGeneralInfoForm.tsx

import { Stack, TextInput, Textarea, Switch, Select, SimpleGrid } from '@mantine/core';
import { useController, type Control, type UseFormWatch } from 'react-hook-form';
import { getLangInfos } from '@lib2/model/common/language/Languages';
import { FEED_CONSTRAINTS, ITUNES_EXPLICIT_VALUES, ITUNES_EXPLICIT_LABELS } from '@/lib/constants/feed';
import type { FeedFormData } from '@/lib/types/Feed';

interface FeedGeneralInfoFormProps {
  control: Control<FeedFormData>;
  watch: UseFormWatch<FeedFormData>;
}

// Build language options from lib2 curated list
const LANGUAGE_OPTIONS = getLangInfos(new Set()).map((lang) => ({
  value: lang.iso2,
  label: lang.englishLabel,
}));

// Build iTunes explicit options
const EXPLICIT_OPTIONS = ITUNES_EXPLICIT_VALUES.map((val) => ({
  value: val,
  label: ITUNES_EXPLICIT_LABELS[val],
}));

export function FeedGeneralInfoForm({ control, watch }: FeedGeneralInfoFormProps) {
  const { field: titleField, fieldState: titleState } = useController({ control, name: 'title' });
  const titleLength = watch('title')?.length ?? 0;

  const { field: descShortField, fieldState: descShortState } = useController({ control, name: 'descShort' });
  const descShortLength = watch('descShort')?.length ?? 0;

  const { field: stateField } = useController({ control, name: 'mediaStateType' });
  const isActive = stateField.value === 'active';

  const { field: languageField } = useController({ control, name: 'language' });
  const { field: explicitField } = useController({ control, name: 'itunesExplicit' });

  return (
    <Stack gap="md">
      <TextInput
        label="Title"
        value={titleField.value}
        onChange={titleField.onChange}
        onBlur={titleField.onBlur}
        error={titleState.error?.message}
        description={`${titleLength}/${FEED_CONSTRAINTS.TITLE_MAX}`}
        maxLength={FEED_CONSTRAINTS.TITLE_MAX}
        required
      />

      <Textarea
        label="Description"
        value={descShortField.value}
        onChange={descShortField.onChange}
        onBlur={descShortField.onBlur}
        error={descShortState.error?.message}
        description={`${descShortLength}/${FEED_CONSTRAINTS.DESC_SHORT_MAX}`}
        maxLength={FEED_CONSTRAINTS.DESC_SHORT_MAX}
        rows={3}
      />

      <Switch
        label="Active"
        description={isActive ? 'Feed is publicly available' : 'Feed is not publicly available'}
        checked={isActive}
        onChange={(e) => stateField.onChange(e.currentTarget.checked ? 'active' : 'inactive')}
      />

      <SimpleGrid cols={2}>
        <Select
          label="Language"
          data={LANGUAGE_OPTIONS}
          value={languageField.value}
          onChange={(val) => val && languageField.onChange(val)}
          allowDeselect={false}
        />
        <Select
          label="Explicit Content"
          data={EXPLICIT_OPTIONS}
          value={explicitField.value}
          onChange={(val) => val && explicitField.onChange(val)}
          allowDeselect={false}
        />
      </SimpleGrid>
    </Stack>
  );
}
